import React from 'react';
import PropTypes from 'prop-types';
import { Loader2, Check } from 'lucide-react';
import './Button.css';

/**
 * Button Component
 * 
 * Sizes (from Figma):
 * - primary (large): 56px height, px: 20px, py: 14px, gap: 8px
 * - secondary (medium): 44px height, px: 16px, py: 10px, gap: 6px, uppercase
 * - tertiary (small): 36px height, px: 12px, py: 6px, gap: 4px, uppercase
 * 
 * Variants:
 * - main: Brand primary green background
 * - alternative: Brand primary alt
 * - danger: Red background for destructive actions
 * - outline: Transparent with border
 * - black: Black background
 * - white: White background
 * - gray: Gray background
 * - text: Transparent, text only
 * - text-danger: Transparent, red text
 * - hero: the signature CTA — gradient pill with an animated rim, 52px tall,
 *   skinned per brand through the --button-hero-* tokens (OVER-LL "Nästa",
 *   1857:31927 / 2041:62600)
 * 
 * States:
 * - default, hover, pressed (active), loading, disabled
 * 
 * Figma nodes:
 * - Primary: 3617:488
 * - Secondary: 3617:707
 * - Tertiary: 3617:926
 */

const Button = ({
  children,
  size = 'primary',
  variant = 'main',
  iconLeft = null,
  iconRight = null,
  iconOnly = false,
  loading = false,
  success = false,
  disabled = false,
  fullWidth = false,
  className = '',
  type = 'button',
  href,
  onClick,
  ...props
}) => {
  // Determine if button is interactive
  const isDisabled = disabled || loading || success;

  // Handle click
  const handleClick = (e) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  // Render icon
  const renderIcon = (icon, position) => {
    if (!icon) return null;
    
    return (
      <span className="button__icon" data-position={position}>
        {icon}
      </span>
    );
  };

  // Render loading spinner
  const renderLoader = () => (
    <span className="button__loader">
      <Loader2 className="button__loader-icon" />
    </span>
  );

  // Render success checkmark (shown briefly after a successful action)
  const renderSuccess = () => (
    <span className="button__success">
      <Check className="button__success-icon" />
    </span>
  );

  // Icon beside a label → that side's padding drops to 2/3 (see Button.css)
  const hasLabel = !iconOnly && children != null && children !== false && children !== '';

  const classes = [
    'button',
    `button--${size}`,
    `button--${variant}`,
    iconOnly && 'button--icon-only',
    hasLabel && iconLeft && 'button--icon-left',
    hasLabel && iconRight && 'button--icon-right',
    loading && 'button--loading',
    success && 'button--success',
    disabled && 'button--disabled',
    fullWidth && 'button--full-width',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {/* Loading overlay */}
      {loading && renderLoader()}

      {/* Success overlay */}
      {success && !loading && renderSuccess()}

      {/* Content */}
      <span className="button__content">
        {renderIcon(iconLeft, 'left')}
        {!iconOnly && children && (
          <span className="button__text">{children}</span>
        )}
        {iconOnly && iconLeft && !iconRight && null}
        {renderIcon(iconRight, 'right')}
      </span>
    </>
  );

  // A link that looks like a button — a real <a>, so the URL works without JS
  // and the router's click interceptor / middle-click / "copy link" all apply
  if (href) {
    return (
      <a
        href={isDisabled ? undefined : href}
        className={classes}
        onClick={handleClick}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={isDisabled}
      onClick={handleClick}
      aria-busy={loading}
      {...props}
    >
      {content}
    </button>
  );
};

Button.propTypes = {
  /** Button content */
  children: PropTypes.node,
  /** Size variant: primary (large), secondary (medium), tertiary (small) */
  size: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  /** Color variant */
  variant: PropTypes.oneOf([
    'main',
    'alternative',
    'danger',
    'outline',
    'black',
    'white',
    'gray',
    'text',
    'text-danger',
    'hero',
  ]),
  /** Icon on the left side */
  iconLeft: PropTypes.node,
  /** Icon on the right side */
  iconRight: PropTypes.node,
  /** Display as icon-only button (square) */
  iconOnly: PropTypes.bool,
  /** Show loading state */
  loading: PropTypes.bool,
  /** Show a brief success checkmark state */
  success: PropTypes.bool,
  /** Disable the button */
  disabled: PropTypes.bool,
  /** Stretch to the container's width (replaces consumer `width: 100%` rules) */
  fullWidth: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Button type attribute */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Renders a real link (<a>) with the button's look — for CTAs that lead to a URL */
  href: PropTypes.string,
  /** Click handler */
  onClick: PropTypes.func,
};

export default Button;
